import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type MultiIfElseNodeType, nodeSchemas } from 'automations/types/nodeTypes';
import { saveConditionNode } from 'automations/utils/conditionNode';
import { updateMultiIfElseNodeCreate } from 'automations/utils/multiIfElseNode';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import AutomationsCriteriaBuilder from '../AutomationsCriteriaBuilder';

export default function CreateMultiIfElseMenu({
  node,
}: {
  node: NodeProps<MultiIfElseNodeType>;
}) {
  const { id } = node;
  const dispatch = useAutomationDispatch();
  const parentId = useSelector(getParentIdSelector(id));
  const { open, setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const handleSave = useCallback(async (criteriaId: number | undefined) => {
    if (!criteriaId) {
      message.error('Please select a condition');
      return;
    }

    try {
      const parsedNodeData = nodeSchemas.MultiIfElseNodeData.parse({
        ...node.data,
        additionalOptions: {
          criteriaGroupId: criteriaId,
        },
      });
      dispatch(addAdditionalOptions({
        nodeId: id,
        additionalOptions: parsedNodeData.additionalOptions!,
      }));
      const response = await dispatch<any>(saveConditionNode(id, parentId, contentMode));
      if (response) dispatch(updateMultiIfElseNodeCreate(response));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
      message.error(errorMessage);
    }
  }, [dispatch, id, node.data, parentId, setMenuOpen, contentMode]);

  return (
    <AutomationsCriteriaBuilder
      title="Condition - Multi If / Else"
      open={open}
      onClose={() => setMenuOpen(false)}
      onSave={handleSave}
      existingCriteriaId={node.data?.additionalOptions?.criteriaGroupId}
      saveButtonText="Set Condition"
      allowEmpty={false}
    />
  );
}
